import { useState } from 'react';

import * as FileSaver from 'file-saver';
import type { AxiosError } from 'axios';

import { BodySmall, Box, FileSelector, FileSelectorFileType, Link, Modal, ModalFooter, ModalHeader, Snackbar } from '@parspec/pixel';

import { useValidateBulkCRMFileMutation } from '../Company/queries';
import { ValidateFileResponse } from '../Company/queries/apiTypes';
import { UPLOAD_CONTACTS_CSV_TEMPLATE } from './constant';

interface ImportListDialogProps {
    open: boolean;
    onClose: () => void;
    handleNextStep: (uploadedData: ValidateFileResponse) => void;
}

const BulkImport = (props: ImportListDialogProps) => {
    const { open, onClose, handleNextStep } = props;

    const [fileRequiredError, setFileRequiredError] = useState<string | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const { mutateAsync: validateFile, isLoading } = useValidateBulkCRMFileMutation();

    const downloadTemplate = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();

        const contentType = 'text/csv';
        setSnackbarMessage('The download has started ! Check your Downloads folder');
        const csvFile = new Blob([UPLOAD_CONTACTS_CSV_TEMPLATE], { type: contentType });
        FileSaver.saveAs(csvFile, 'Parspec CRM Bulk Import Format.csv');
    };

    const onUploadFile = async (files: File[] | FileSelectorFileType[]) => {
        if (files.length) {
            setFileRequiredError(null);
            const formData = new FormData();
            formData.append('file', files[0] as File);

            try {
                const response = await validateFile(formData);
                handleNextStep(response.data);
            } catch (error) {
                const axiosError = error as AxiosError<{ message?: string }>;
                if (axiosError.status !== 400) return;

                const errorMsg = axiosError.response?.data.message;
                if (errorMsg) setFileRequiredError(errorMsg);
            }
        }
    };

    const onDeleteFile = () => {
        setFileRequiredError(null);
    };

    return (
        <>
            <Snackbar
                open={Boolean(snackbarMessage)}
                onClose={() => setSnackbarMessage('')}
                message={snackbarMessage}
            />
            <Modal
                open={open}
                borderRadius="6px"
                header={(
                    <ModalHeader title="Import Contacts & Companies" onClose={onClose}>
                        <Box width={680}>
                            <BodySmall limit={false} sx={{ lineHeight: "1.25rem" }} color="text.secondary">
                                Download this <Link sx={{ cursor: "pointer" }} onClick={downloadTemplate}>File Template</Link> for unified import of both contacts and companies. The template provides headers and sample data for your convenience.
                            </BodySmall>
                        </Box>
                    </ModalHeader>
                )}
                footer={<ModalFooter onReject={onClose} />}
            >
                <Box width={680} marginTop={2}>
                    <FileSelector
                        placeholder={
                            <BodySmall limit={false}>
                                Drag and drop a file, or
                            </BodySmall>
                        }
                        onSelect={onUploadFile}
                        acceptedFormats={['.xls', '.xlsx', '.csv']}
                        helperText={'Supported file types: XLS, XLSX, CSV'}
                        onDeleteFile={onDeleteFile}
                        isLoading={isLoading}
                    />
                    {fileRequiredError && (
                        <BodySmall limit={false} color="error.main" pt={3}>
                            {fileRequiredError}
                        </BodySmall>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default BulkImport;
